import React, { useRef, useState } from 'react'

import { RESOURCE_TYPE_CODE_PREFIX } from '~/client/src/shared/constants/resourceTypes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { toast } from '~/client/src/shared/utils/toast'

import { buildTemplatePropertyInputs } from './formProperties'
import type { IFormPropertiesShellSubmit } from './FormPropertiesShell'
import { FormPropertiesShell } from './FormPropertiesShell'
import { useNewFormDefaults } from './useNewFormDefaults'

interface IProps {
  resourceTypeId: string
  onClose: () => void
  onCreated: (newTemplateId: string) => void | Promise<void>
  onBackToPicker?: () => void
  resourceSchema: ResourceSchema
}

export const CreateFormSidebar: React.FC<IProps> = ({
  resourceTypeId,
  onClose,
  onCreated,
  onBackToPicker,
  resourceSchema,
}) => {
  const { properties, loading } = useNewFormDefaults({
    resourceTypeId,
    resourceSchema,
  })

  const [saving, setSaving] = useState(false)

  const hasLoadedOnceRef = useRef(false)
  if (!loading) hasLoadedOnceRef.current = true
  const shellKey = hasLoadedOnceRef.current ? 'ready' : 'loading'

  const handleCreate = async ({
    name,
    properties: propertyEdits,
  }: IFormPropertiesShellSubmit) => {
    if (!resourceTypeId) return

    setSaving(true)
    try {
      const input = {
        name,
        description: '',
        codePrefix: RESOURCE_TYPE_CODE_PREFIX[resourceTypeId] || null,
        properties: buildTemplatePropertyInputs(propertyEdits),
      }
      const created = await resourceSchema.createForm(input)
      if (created._tag === 'Committed') {
        if (onBackToPicker) onBackToPicker()
        else onClose()
        return
      }
      if (created.formId) {
        await onCreated(created.formId)
      } else {
        onClose()
      }
    } catch (error) {
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
      console.error(error)
      setSaving(false)
    }
  }

  return (
    <FormPropertiesShell
      key={shellKey}
      headerTitle={Localization.translator.createNewForm}
      submitLabel={Localization.translator.create}
      resourceTypeId={resourceTypeId}
      properties={properties}
      loading={loading}
      saving={saving}
      initialName=""
      autoFocusName={true}
      onClose={onClose}
      onBackToPicker={onBackToPicker}
      onSubmit={handleCreate}
    />
  )
}
